import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { createProgressRecorder } from "../utils/propertyApplicationProgress";

export default function usePropertyApplicationProgress(userId, snapshot, step, started) {
  const [status, setStatus] = useState("idle");
  const hasStarted = useRef(false);
  const recorder = useMemo(() => {
    if (!userId) return null;
    let storage;
    try { storage = window.sessionStorage; } catch { storage = null; }
    return createProgressRecorder({
      userId, storage, onStatus: setStatus,
      send: async (payload, keepalive) => {
        const response = await fetch("https://www.smart-lend.com/api/propTermLoan/application-progress", {
          method: "POST", keepalive,
          headers: {
            "Content-Type": "application/json", Accept: "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Progress save failed");
        return response.json();
      },
    });
  }, [userId]);
  const serialized = JSON.stringify(snapshot);
  useLayoutEffect(() => {
    if (started) hasStarted.current = true;
    if (hasStarted.current) recorder?.capture(JSON.parse(serialized), step);
  }, [recorder, serialized, step, started]);
  useEffect(() => {
    const timeout = setTimeout(() => recorder?.flush(), 600);
    return () => clearTimeout(timeout);
  }, [recorder, serialized, step, started]);
  useEffect(() => {
    if (!recorder) return;
    const flush = () => recorder.flush(true);
    const hidden = () => { if (document.visibilityState === "hidden") flush(); };
    const online = () => recorder.flush();
    const interval = setInterval(online, 5000);
    window.addEventListener("pagehide", flush);
    window.addEventListener("online", online);
    document.addEventListener("visibilitychange", hidden);
    recorder.flush(); // Retry unsent snapshots from an earlier visit in this tab.
    return () => {
      clearInterval(interval);
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("online", online);
      document.removeEventListener("visibilitychange", hidden);
      flush(); // Includes navigation within the React app.
    };
  }, [recorder]);
  return { status, sessionId: recorder?.sessionId, complete: () => { hasStarted.current = false; recorder?.complete(); } };
}
